/**
 * AddEventStudentDialog
 *
 * Modal launched from the EventStudentsPanel "Add student" button.
 * Lets an authorized operator attach an existing RosterPerson
 * (student) to an active reunification event via
 * `useCreateEventStudent` (POST `/events/{event_public_id}/students/`).
 *
 * The picker (`StudentPicker`) excludes RosterPersons already on the
 * event so the operator can't double-add. Optional initial notes and
 * the two interview/counseling flags are surfaced so a late-arriving
 * student with known LEO/counseling requirements can be flagged on
 * the way in (matches the field set on the inline edit form in
 * `StudentDetailDrawer`).
 *
 * Compliance gates (Restricted-Student-Record-PII):
 * - `medical_notes` is intentionally NOT exposed in the add dialog —
 *   it's an edit-only field accessible from the detail drawer once
 *   the row exists. Capturing medical notes during initial add adds
 *   a second compliance surface for very little operator benefit
 *   (the field can still be filled within seconds in the drawer).
 * - Backend writes a `student.added` row to `EventAssignmentLog` on
 *   create (FERPA audit-log). No additional client-side logging is
 *   required.
 * - Backend permission gate `CanOperateStudentCheckIn` is the
 *   load-bearing check; the feature-flag check in the parent panel
 *   is UI-only.
 *
 * Out of scope:
 * - Ad-hoc walk-ins (creating a new RosterPerson on the fly)
 * - Bulk add
 * - Photo capture
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  Field,
  HStack,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { useCreateEventStudent } from '../../hooks/queries/useReunification';
import { toaster } from '../ui/toaster';
import type { StudentReunification } from '../../types/ReunificationTypes';
import { StudentPicker, type StudentPickerSelection } from './StudentPicker';

export interface AddEventStudentDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog should close (cancel, escape, backdrop click). */
  onClose: () => void;
  /** Reunification event public_id receiving the new student row. */
  eventId: string;
  /**
   * RosterPerson ids already on the event; excluded from the picker
   * so the operator can't create duplicate rows.
   */
  existingRosterPersonIds?: number[];
  /** Called after a successful create with the new student row. */
  onCreated?: (created: StudentReunification) => void;
}

export function AddEventStudentDialog({
  isOpen,
  onClose,
  eventId,
  existingRosterPersonIds,
  onCreated,
}: AddEventStudentDialogProps) {
  const [selected, setSelected] = useState<StudentPickerSelection | null>(null);
  const [notes, setNotes] = useState('');
  const [requiresLeoInterview, setRequiresLeoInterview] = useState(false);
  const [requiresCounseling, setRequiresCounseling] = useState(false);

  const createMutation = useCreateEventStudent(eventId);
  const submitting = createMutation.isPending;

  // Reset state every time the dialog opens — avoid leaking the
  // prior pick into a subsequent add.
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setNotes('');
      setRequiresLeoInterview(false);
      setRequiresCounseling(false);
    }
  }, [isOpen]);

  const canSubmit = useMemo(
    () => !!selected && !submitting,
    [selected, submitting],
  );

  async function handleSubmit() {
    if (!selected) return;
    try {
      const created = await createMutation.mutateAsync({
        roster_person: selected.rosterPersonId,
        notes: notes.trim() || undefined,
        requires_leo_interview: requiresLeoInterview || undefined,
        requires_counseling: requiresCounseling || undefined,
      });
      toaster.create({
        title: 'Student added',
        description: `${selected.name} added to event.`,
        type: 'success',
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not add student',
        description: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open && !submitting) onClose();
      }}
      placement="center"
      size="md"
      closeOnInteractOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bgColor="surface.primary"
            borderRadius="lg"
            maxWidth="560px"
            data-e2e="add-event-student-dialog"
          >
            <Dialog.Header p="20px 20px 8px 20px">
              <Dialog.Title
                fontSize="16px"
                fontWeight={600}
                color="text.primary"
              >
                Add student to event
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body p="0 20px 16px 20px">
              <VStack align="stretch" gap={4}>
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="text.secondary"
                    mb={2}
                  >
                    Student
                  </Text>
                  <StudentPicker
                    selected={selected}
                    onSelect={setSelected}
                    excludeRosterPersonIds={existingRosterPersonIds}
                    dataE2ePrefix="add-event-student-picker"
                  />
                </Box>

                <Field.Root>
                  <Field.Label fontSize="sm" color="text.primary">
                    Initial notes (optional)
                  </Field.Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any context the team should know at intake…"
                    rows={3}
                    disabled={submitting}
                    data-e2e="add-event-student-notes"
                  />
                </Field.Root>

                <VStack align="stretch" gap={2}>
                  <Checkbox.Root
                    checked={requiresLeoInterview}
                    onCheckedChange={(details) =>
                      setRequiresLeoInterview(details.checked === true)
                    }
                    disabled={submitting}
                    data-e2e="add-event-student-requires-leo"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize="sm">
                      Requires LEO interview
                    </Checkbox.Label>
                  </Checkbox.Root>
                  <Checkbox.Root
                    checked={requiresCounseling}
                    onCheckedChange={(details) =>
                      setRequiresCounseling(details.checked === true)
                    }
                    disabled={submitting}
                    data-e2e="add-event-student-requires-counseling"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize="sm">
                      Requires counseling
                    </Checkbox.Label>
                  </Checkbox.Root>
                </VStack>

                <Text fontSize="xs" color="text.tertiary">
                  Medical notes and supervising-staff assignment can be set
                  from the student detail drawer once the row exists. Medical
                  notes require a separate medical-staff role
                  (Restricted-Student-Record-PII per FERPA).
                </Text>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer p="0 20px 20px 20px">
              <HStack justify="flex-end" gap={2} width="100%">
                <Button
                  visual="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  data-e2e="add-event-student-cancel"
                >
                  Cancel
                </Button>
                <Button
                  visual="primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  data-e2e="add-event-student-submit"
                >
                  {submitting ? 'Adding…' : 'Add student'}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default AddEventStudentDialog;
